// /tourguide shows the TourGuide (TG) icon set; every other route keeps the
// YourTour (YT) set that public/index.html links by default (issue #1985). The
// TG set in public/tg-icons/ mirrors the YT files name for name, so a swap only
// rewrites the directory. It is not public/tourguide/: a static directory of
// that name would shadow the /tourguide route.
const TOURGUIDE_PATH = /^\/tourguide\/?$/;
const TG_ICON_DIR = `${process.env.PUBLIC_URL || ''}/tg-icons`;

// Every head element that points at an icon-set file.
const SWAPPABLE_SELECTOR = 'link[rel*="icon"], link[rel="manifest"], meta[name="msapplication-config"]';

// Non-production hosts replace every icon with a colored badge so tabs from
// different environments can be told apart at a glance.
const ENV_BADGE_COLORS = [
  [/localhost:3000$/, 'olive'],
  [/localhost:3001$/, 'teal'],
  [/viewyourtour\.(org|net)$/, 'rebeccapurple'],
];

// Both glyphs share one bounding box (the TG mark reuses the YT T and cap
// height), so the TG paths only need shifting onto the YT origin.
const BADGE_GLYPHS = {
  yt: '<g fill="white" transform="translate(3.5,8.75) scale(0.0995)">'
    + '<path d="M44.9,93.2L0,0h33.3l27.3,62.2L87.9,0h33.3l-44.9,93.2v52.4h-31.4v-52.4Z"/>'
    + '<path d="M181.3,25.5h-38.8V0h108.7v25.5h-38.6v120.2h-31.4V25.5Z"/>'
    + '</g>',
  tg: '<g fill="white" transform="translate(3.5,8.75) scale(0.0995) translate(-4,-54)">'
    + '<path transform="translate(-142.3 260) scale(.1 -.1)" d="M1466 2052c-2-4-3-62-3-129v-123h387v-600c0-393 3-600 10-601 69-6 293-2 299 6 4 6 7 277 7 603l-1 592h385v257h-540c-297 1-542-2-544-5z"/>'
    + '<path d="M191.2 54.2c-31.3 0-57 25.7-57 57v32.2c0 31.3 25.7 57 57 57h6.8c31.3 0 57-25.7 57-57V125h-59v30h26.36c-4.3 9.14-13.46 15.4-24.36 15.4h-6.8c-15.09 0-27-11.91-27-27v-32.2c0-15.09 11.91-27 27-27h6.8c7.17 0 14.02 2.84 19.09 7.91l21.22-21.22C227.62 60.21 213.11 54.2 198 54.2z"/>'
    + '</g>',
};

export const isTourGuidePath = (pathname) => TOURGUIDE_PATH.test(pathname || '');

export const getEnvBadgeColor = (host) => {
  const match = ENV_BADGE_COLORS.find(([pattern]) => pattern.test(host || ''));
  return match ? match[1] : null;
};

const createBadgeHref = (color, glyph) => {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">'
    + `<rect width="32" height="32" rx="7" fill="${color}"/>${BADGE_GLYPHS[glyph]}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Point the document's icon links at the set for this route.
 *
 * Each element's original value is kept in data-yt-default on first sight, so
 * leaving /tourguide restores exactly what index.html linked.
 *
 * @param {string} pathname - Router pathname
 * @param {Document} doc - Document to update
 * @param {string} host - Host used to pick the environment badge
 */
export const applyRouteFavicon = (pathname, doc = document, host = window.location.host) => {
  const isTourGuide = isTourGuidePath(pathname);
  const badgeColor = getEnvBadgeColor(host);
  const badgeHref = badgeColor ? createBadgeHref(badgeColor, isTourGuide ? 'tg' : 'yt') : null;

  doc.querySelectorAll(SWAPPABLE_SELECTOR).forEach((element) => {
    const attribute = element.tagName === 'META' ? 'content' : 'href';
    if (!element.hasAttribute('data-yt-default')) {
      element.setAttribute('data-yt-default', element.getAttribute(attribute) || '');
    }
    const defaultValue = element.getAttribute('data-yt-default');
    const isIcon = element.tagName === 'LINK' && element.rel.includes('icon');

    let value = isTourGuide ? `${TG_ICON_DIR}/${defaultValue.split('/').pop()}` : defaultValue;
    if (isIcon && badgeHref) {
      value = badgeHref;
    }

    if (element.getAttribute(attribute) !== value) {
      element.setAttribute(attribute, value);
    }
  });
};
